import { UseFormReturn, Controller } from 'react-hook-form';
import { Input } from '@theraply/ui';
import { CurrencyDollarIcon, MapPinIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { PracticeDetailsData } from '../../../hooks/useRegistrationWizard';

interface PracticeDetailsStepProps {
  form: UseFormReturn<PracticeDetailsData>;
}

const SPECIALTIES = [
  'Anxiety',
  'Depression',
  'Trauma',
  'PTSD',
  'Couples Therapy',
  'Family Therapy',
  'Grief and Loss',
  'Stress Management',
  'Addiction',
  'Eating Disorders',
  'OCD',
  'Bipolar Disorder',
  'ADHD',
  'Autism Spectrum',
  'Chronic Illness',
  'Life Transitions',
  'Parenting',
  'Self-Esteem',
  'Sleep Disorders',
  'Anger Management',
];

const TREATMENT_APPROACHES = [
  'Cognitive Behavioral Therapy (CBT)',
  'Dialectical Behavior Therapy (DBT)',
  'Eye Movement Desensitization and Reprocessing (EMDR)',
  'Psychodynamic',
  'Humanistic',
  'Mindfulness-Based',
  'Solution-Focused',
  'Acceptance and Commitment Therapy (ACT)',
  'Trauma-Focused CBT',
  'Family Systems',
  'Gestalt',
  'Psychoanalytic',
  'Narrative Therapy',
  'Play Therapy',
  'Art Therapy',
  'Integrative',
];

interface MultiSelectCheckboxProps {
  label: string;
  options: string[];
  value: string[];
  onChange: (value: string[]) => void;
  error?: string;
  required?: boolean;
}

function MultiSelectCheckbox({ label, options, value, onChange, error, required }: MultiSelectCheckboxProps) {
  const handleToggle = (option: string) => {
    if (value.includes(option)) {
      onChange(value.filter((v) => v !== option));
    } else {
      onChange([...value, option]);
    }
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-4">
        {options.map((option) => (
          <label
            key={option}
            className={clsx(
              'relative flex items-start cursor-pointer rounded-lg border p-3 transition-colors',
              value.includes(option)
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-300 hover:border-gray-400'
            )}
          >
            <div className="flex items-center h-5">
              <input
                type="checkbox"
                checked={value.includes(option)}
                onChange={() => handleToggle(option)}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
            </div>
            <div className="ml-3 text-sm">
              <span className={clsx('font-medium', value.includes(option) ? 'text-primary-900' : 'text-gray-900')}>
                {option}
              </span>
            </div>
          </label>
        ))}
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}

export function PracticeDetailsStep({ form }: PracticeDetailsStepProps) {
  const {
    register,
    control,
    watch,
    formState: { errors },
  } = form;

  const sessionTypes = watch('sessionTypes');

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Practice Details</h2>
        <p className="mt-2 text-sm text-gray-600">
          Tell us about your practice, specialties, and how you work with clients.
        </p>
      </div>

      <Controller
        name="specialties"
        control={control}
        render={({ field }) => (
          <MultiSelectCheckbox
            label="Specialties"
            options={SPECIALTIES}
            value={field.value}
            onChange={field.onChange}
            error={errors.specialties?.message}
            required
          />
        )}
      />

      <Controller
        name="treatmentApproaches"
        control={control}
        render={({ field }) => (
          <MultiSelectCheckbox
            label="Treatment Approaches"
            options={TREATMENT_APPROACHES}
            value={field.value}
            onChange={field.onChange}
            error={errors.treatmentApproaches?.message}
            required
          />
        )}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <Input
            {...register('sessionRate')}
            type="text"
            label="Hourly Session Rate"
            placeholder="150.00"
            required
            error={errors.sessionRate?.message}
            startIcon={<CurrencyDollarIcon className="h-5 w-5" />}
            helperText="Your standard hourly rate in USD"
            fullWidth
          />
        </div>
      </div>

      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          Session Types <span className="text-red-500">*</span>
        </label>
        <div className="space-y-2">
          <Controller
            name="sessionTypes"
            control={control}
            render={({ field }) => (
              <>
                <label className="flex items-center space-x-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={field.value.includes('in-person')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        field.onChange([...field.value, 'in-person']);
                      } else {
                        field.onChange(field.value.filter((v) => v !== 'in-person'));
                      }
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-900 group-hover:text-primary-700">
                    In-Person Sessions
                  </span>
                </label>

                <label className="flex items-center space-x-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={field.value.includes('video')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        field.onChange([...field.value, 'video']);
                      } else {
                        field.onChange(field.value.filter((v) => v !== 'video'));
                      }
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-900 group-hover:text-primary-700">
                    Video Sessions (Telehealth)
                  </span>
                </label>

                <label className="flex items-center space-x-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={field.value.includes('both')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        field.onChange(['both']);
                      } else {
                        field.onChange(field.value.filter((v) => v !== 'both'));
                      }
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-900 group-hover:text-primary-700">
                    Both In-Person and Video
                  </span>
                </label>
              </>
            )}
          />
        </div>
        {errors.sessionTypes && (
          <p className="text-sm text-red-600">{errors.sessionTypes.message}</p>
        )}
      </div>

      {(sessionTypes.includes('in-person') || sessionTypes.includes('both')) && (
        <div>
          <Input
            {...register('officeAddress')}
            label="Office Address"
            placeholder="123 Main St, City, State ZIP"
            error={errors.officeAddress?.message}
            startIcon={<MapPinIcon className="h-5 w-5" />}
            helperText="Enter your office address for in-person sessions"
            fullWidth
          />
        </div>
      )}

      <div>
        <label htmlFor="bio" className="block text-sm font-medium text-gray-700">
          Professional Bio <span className="text-red-500">*</span>
        </label>
        <p className="text-sm text-gray-500 mt-1">
          Write a brief bio about your experience, approach, and what clients can expect working with you.
        </p>
        <textarea
          {...register('bio')}
          id="bio"
          rows={6}
          maxLength={500}
          placeholder="I am a licensed therapist with over 10 years of experience..."
          className={clsx(
            'mt-2 block w-full rounded-lg border px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-1 transition-colors',
            errors.bio
              ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
          )}
        />
        <div className="flex justify-between items-center mt-1.5">
          <div>
            {errors.bio && <p className="text-sm text-red-600">{errors.bio.message}</p>}
          </div>
          <p className="text-sm text-gray-500">{watch('bio')?.length || 0}/500</p>
        </div>
      </div>

      <div className="rounded-lg bg-purple-50 p-4 border border-purple-200">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-purple-400"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M10 9a3 3 0 100-6 3 3 0 000 6zM6 8a2 2 0 11-4 0 2 2 0 014 0zM1.49 15.326a.78.78 0 01-.358-.442 3 3 0 014.308-3.516 6.484 6.484 0 00-1.905 3.959c-.023.222-.014.442.025.654a4.97 4.97 0 01-2.07-.655zM16.44 15.98a4.97 4.97 0 002.07-.654.78.78 0 00.357-.442 3 3 0 00-4.308-3.517 6.484 6.484 0 011.907 3.96 2.32 2.32 0 01-.026.654zM18 8a2 2 0 11-4 0 2 2 0 014 0zM5.304 16.19a.844.844 0 01-.277-.71 5 5 0 019.947 0 .843.843 0 01-.277.71A6.975 6.975 0 0110 18a6.974 6.974 0 01-4.696-1.81z" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-purple-800">Client Visibility</h3>
            <div className="mt-2 text-sm text-purple-700">
              <p>
                Your bio, specialties, and treatment approaches will be visible to potential clients
                browsing therapists on our platform. Make sure to highlight what makes your practice
                unique and the types of clients you work best with.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
